import { inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfigService } from '../core/services/config.service';
import { analytics } from '../../environments/analytics';
import { ANALYTICS_IDENTITY_EVENT } from './analytics-identity';

interface AnalyticsIdentity {
  userId: string | null;
  tenantUuid: string | null;
}

interface AnalyticsPage {
  path: string;
  location: string;
  title: string;
  referrer: string;
}

/** One browser-only owner for initialization, SPA views, and current identity. */
@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService implements OnDestroy {
  private started = false;
  private configured = false;
  private failed = false;
  private tagLoaded = false;
  private destroyed = false;
  private pendingEvents = 0;
  private identityRevision = 0;
  private identityRefresh: Promise<void> = Promise.resolve();
  private identity: AnalyticsIdentity = { userId: null, tenantUuid: null };
  private subscription: Subscription | null = null;
  private previousLocation = '';
  private page: AnalyticsPage | null = null;
  private lastNavigationId: number | null = null;
  private readonly refreshListener = () => { void this.refreshIdentity(); };

  private router = inject(Router);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private config = inject(ConfigService);

  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId) || !analytics.enabled) {
      return;
    }
    const win = this.document.defaultView;
    if (!win || /^(localhost|127\.0\.0\.1|\[::1\])$/.test(win.location.hostname) ||
        win.location.hostname.endsWith('.local')) {
      return;
    }
    this.started = true;
    win.addEventListener(ANALYTICS_IDENTITY_EVENT, this.refreshListener);
    win.addEventListener('storage', this.refreshListener);
    this.document.addEventListener('visibilitychange', this.refreshListener);
    this.subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && this.lastNavigationId !== event.id) {
        this.lastNavigationId = event.id;
        this.trackPageView();
      }
    });
    // Covers bootstraps where initial navigation completed before the root component.
    if (this.router.navigated && this.lastNavigationId === null) {
      this.trackPageView();
    }
  }

  /** Only catalogued feature events, with an allowlist of non-content parameters. */
  trackEvent(name: string, parameters: Record<string, string | number | boolean> = {}): void {
    if (!this.started || this.destroyed || this.failed || !/^feature_(start|step|complete|error)$/.test(name)) {
      return;
    }
    const allowed = ['feature_id', 'workflow_id', 'attempt_id', 'step_id', 'result', 'error_code'];
    const safe: Record<string, string | number | boolean> = {};
    allowed.forEach(key => {
      const value = parameters[key];
      if ((typeof value === 'string' && /^[a-zA-Z0-9_.:-]{1,100}$/.test(value)) ||
          typeof value === 'boolean' || (typeof value === 'number' && isFinite(value))) {
        safe[key] = value;
      }
    });
    this.sendEvent(name, this.page || this.currentPage(), safe);
  }

  /** Storage adapters notify after auth writes/clears, including same-tab switches. */
  refreshIdentity(): Promise<void> {
    if (!this.started || this.destroyed || this.failed) {
      return Promise.resolve();
    }
    const revision = ++this.identityRevision;
    this.identityRefresh = Promise.resolve().then(() => this.readIdentity()).catch(() => ({
      userId: null, tenantUuid: null,
    })).then(identity => {
      if (revision !== this.identityRevision || this.destroyed) {
        return;
      }
      const changed = identity.userId !== this.identity.userId || identity.tenantUuid !== this.identity.tenantUuid;
      this.identity = identity;
      if (changed && this.configured && this.page) {
        this.configure(this.page);
      }
    });
    return this.identityRefresh;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.identityRevision++;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const win = this.document.defaultView;
    if (win) {
      win.removeEventListener(ANALYTICS_IDENTITY_EVENT, this.refreshListener);
      win.removeEventListener('storage', this.refreshListener);
    }
    this.document.removeEventListener('visibilitychange', this.refreshListener);
  }

  private trackPageView(): void {
    const page = this.currentPage();
    page.referrer = this.previousLocation || page.referrer;
    this.previousLocation = page.location;
    this.page = page;
    this.sendEvent('page_view', page);
  }

  private sendEvent(name: string, page: AnalyticsPage, parameters: object = {}): void {
    if (!this.started || this.destroyed || this.failed || this.pendingEvents >= 50) {
      return;
    }
    this.pendingEvents++;
    this.refreshIdentity().then(async () => {
      // Never send under an old identity while a newer auth write is resolving.
      let latest: Promise<void>;
      do {
        latest = this.identityRefresh;
        await latest;
      } while (latest !== this.identityRefresh);
      if (this.destroyed || this.failed) {
        return;
      }
      if (!this.configured) {
        this.configure(page);
        this.loadTag();
      } else if (name === 'page_view') {
        // Also sanitizes the defaults used by automatically collected events.
        this.configure(page);
      }
      this.command('event', name, {
        ...this.context(page),
        ...parameters,
        send_to: analytics.measurementId,
      });
    }).catch(() => {
      // Analytics must never interrupt navigation or authentication.
    }).then(() => { this.pendingEvents--; });
  }

  private configure(page: AnalyticsPage): void {
    this.command('config', analytics.measurementId, {
      ...this.context(page),
      send_page_view: false,
      user_id: this.identity.userId,
      user_properties: { tenant_uuid: this.identity.tenantUuid },
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...(analytics.environment === 'prod' ? {} : { debug_mode: true }),
    });
    this.configured = true;
  }

  private context(page: AnalyticsPage): object {
    return {
      product_id: analytics.productId,
      app_variant: analytics.appVariant,
      app_environment: analytics.environment,
      tracking_version: '1',
      tenant_uuid: this.identity.tenantUuid,
      page_key: page.path,
      page_path: page.path,
      page_location: page.location,
      page_title: page.title,
      page_referrer: page.referrer,
    };
  }

  private command(...args: any[]): void {
    const win: any = this.document.defaultView;
    if (!win || win['ga-disable-' + analytics.measurementId]) {
      return;
    }
    try {
      if (typeof win.gtag !== 'function') {
        const canQueue = () => this.tagLoaded || win.google_tag_manager || win.dataLayer.length < 100;
        win.dataLayer = win.dataLayer || [];
        win.gtag = function() {
          // Bound memory when the remote script is blocked or never finishes loading.
          if (canQueue()) {
            // eslint-disable-next-line prefer-rest-params -- Preserve the documented gtag arguments-object queue format.
            win.dataLayer.push(arguments);
          }
        };
        win.gtag('js', new Date());
      }
      win.gtag(...args);
    } catch (_) { /* Existing third-party tracking cannot break the app. */ }
  }

  private loadTag(): void {
    const id = 'sokrates-ga4';
    if (this.document.getElementById(id)) {
      return;
    }
    // Reuse a host-owned gtag loader, but configure only this product's destination.
    if (this.document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      return;
    }
    const script = this.document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + analytics.measurementId;
    script.onload = () => { this.tagLoaded = true; };
    script.onerror = () => { this.failed = true; };
    this.document.head.appendChild(script);
  }

  private currentPage(): AnalyticsPage {
    const win = this.document.defaultView;
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig) {
        // Route templates preserve static menu names while replacing dynamic IDs,
        // names, tokens, matrix params, and unmatched URL content.
        const template = route.routeConfig.path;
        segments.push(template === '**' || template === undefined ? ':unmatched' : template);
      }
      route = route.children.find(child => child.outlet === 'primary') || null;
    }
    const base = new URL(this.document.baseURI).pathname.replace(/\/$/, '');
    const path = (base + '/' + segments.filter(Boolean).join('/')).replace(/\/{2,}/g, '/');
    let referrer = '';
    try {
      // Initial external attribution retains the origin, without potentially private paths.
      referrer = this.document.referrer ? new URL(this.document.referrer).origin + '/' : '';
    } catch (_) { /* Malformed referrers are omitted. */ }
    return {
      path,
      location: (win ? win.location.origin : '') + path,
      title: analytics.productId + ' | ' + path,
      referrer,
    };
  }

  private normalizeIdentity(user: any, tenant: any = null): AnalyticsIdentity {
    const safeId = (value: any): string | null => typeof value === 'string' &&
      /^[a-zA-Z0-9_-]{8,100}$/.test(value) ? value : null;
    return {
      // Numeric tenant-local user_id values are deliberately not global user IDs.
      userId: safeId(user && (user.sokrates_user_uuid || user.user_uuid || user.uuid)),
      tenantUuid: safeId(user && user.tenant_uuid) || safeId(tenant),
    };
  }

  private async readIdentity(): Promise<AnalyticsIdentity> {
    return this.normalizeIdentity(this.config.getUser(), this.config.getTenantUuid());
  }
}
